import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, InputAdornment, CircularProgress } from '@mui/material';
import { Email, Lock, CheckCircle, Cancel } from '@mui/icons-material';
import api from '../../api/axiosInstance';


export const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const passwordLongEnough = form.password.length >= 8;
    const passwordsMatch = form.confirmPassword.length > 0 && form.password === form.confirmPassword;
    const passwordsMismatch = form.confirmPassword.length > 0 && form.password !== form.confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!passwordLongEnough) {
            setError('Password must be at least 8 characters');
            return;
        }
        if (form.password !== form.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await api.post(`/auth/reset-password/${token}`, { password: form.password });
            navigate('/login', { state: { message: 'Password reset successfully. Please log in.' } });
        } catch (err) {
            setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" fontWeight={700} mb={1.5}>Invalid link</Typography>
                <Typography color="text.secondary" mb={3}>This reset link is invalid or has expired.</Typography>
                <Button variant="outlined" component={Link} to="/forgot-password" fullWidth>Request new link</Button>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h3" fontWeight={700}>Reset password</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Enter your new password below.</Typography>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="New password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    required
                    error={form.password.length > 0 && !passwordLongEnough}
                    helperText={
                        form.password.length > 0 && !passwordLongEnough
                            ? "Must be at least 8 characters"
                            : "Min. 8 characters"
                    }
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                        },
                    }}
                />
                <TextField
                    label="Confirm new password"
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    required
                    error={passwordsMismatch}
                    helperText={
                        passwordsMismatch
                            ? "Passwords do not match"
                            : passwordsMatch
                                ? "Passwords match"
                                : ""
                    }
                    slotProps={{
                        formHelperText: {
                            sx: { color: passwordsMatch ? "success.main" : undefined },
                        },
                        input: {
                            startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                            endAdornment: form.confirmPassword.length > 0 && (
                                <InputAdornment position="end">
                                    {passwordsMatch ? (
                                        <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />
                                    ) : (
                                        <Cancel sx={{ fontSize: 18, color: "error.main" }} />
                                    )}
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !passwordLongEnough || !passwordsMatch}
                    sx={{ py: 1.375 }}
                >
                    {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Reset Password'}
                </Button>
            </Box>
        </Box>
    );
};


export default ResetPasswordPage;